import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { CalendarCheck, GraduationCap, ShoppingBag, MessageSquare, Smartphone, Users, ArrowRight, Clock } from 'lucide-react';

export default function AdminDashboard() {
  const { apiClient } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/admin/stats');
      setStats(data);
    } catch {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const cards = stats ? [
    { label: 'Total Bookings', value: stats.bookings, icon: CalendarCheck, to: '/admin/bookings', color: 'from-blue-500 to-blue-700', badge: `${stats.pending_bookings} pending` },
    { label: 'Courses', value: stats.courses, icon: GraduationCap, to: '/admin/courses', color: 'from-emerald-500 to-emerald-700' },
    { label: 'Products', value: stats.products, icon: ShoppingBag, to: '/admin/products', color: 'from-violet-500 to-violet-700' },
    { label: 'Contact Inquiries', value: stats.contacts, icon: MessageSquare, to: '/admin/contacts', color: 'from-orange-500 to-orange-600' },
    { label: 'App Leads', value: stats.app_leads, icon: Smartphone, to: '/admin/app-leads', color: 'from-pink-500 to-pink-700' },
    { label: 'Subscribers', value: stats.subscribers, icon: Users, to: '/admin/subscribers', color: 'from-teal-500 to-teal-700' },
  ] : [];

  return (
    <div data-testid="admin-dashboard-page">
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-white">Dashboard</h1>
        <p className="text-white/50 text-sm">Overview of your Giving Hope platform</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card) => (
            <Link
              key={card.label}
              to={card.to}
              className="group relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
              data-testid={`admin-stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                  <card.icon size={20} className="text-white" />
                </div>
                <ArrowRight size={16} className="text-white/20 group-hover:text-white/60 transition-colors" />
              </div>
              <p className="text-3xl font-heading font-bold text-white mb-1">{card.value}</p>
              <p className="text-white/50 text-sm">{card.label}</p>
              {card.badge && (
                <span className="mt-3 inline-flex items-center gap-1 text-xs bg-yellow-500/20 text-yellow-300 px-2.5 py-1 rounded-full">
                  <Clock size={10} /> {card.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
